import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { CharacterPortrait } from '@/components/game/CharacterPortrait';
import { CoinBadge } from '@/components/game/CoinBadge';
import { TimerDisplay } from '@/components/game/TimerDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { duckMusic, isMusicMuted, playMusic, playSfx, restoreMusicVolume, setMusicMuted, setMusicVolume, stopMusic } from '@/lib/audio';
import { toast } from 'sonner';
import { Lock, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import {
  subscribeToMatch,
  subscribeToPlayers,
  startAuctionTimer,
  forceLockBids,
  forfeitMatch,
} from '@/lib/multiplayer';
import {
  submitBidV2,
  lockBidV2,
  resolveAuctionV2,
  pollAuctionTimer,
} from '@/lib/multiplayer-v2';
import { Character } from '@/data/characters';
import { getSupabaseClient } from '@/lib/supabase';

type AuctionPhase = 'reveal' | 'bidding' | 'result';

const QUICK_MESSAGES = [
  { label: '😂 LMAO',           text: '😂 LMAO' },
  { label: '😤 EZ',             text: '😤 EZ' },
  { label: '🎉 YESSS!',         text: '🎉 YESSS!' },
  { label: '😭 NOOO!',          text: '😭 NOOO!' },
  { label: '😮 No way',         text: '😮 No way' },
  { label: '⏩ HURRY UP',       text: '⏩ HURRY UP' },
  { label: '🙏 Please',         text: '🙏 Please' },
  { label: '😂 haha',           text: '😂 haha' },
  { label: '😐 Seriously?',     text: '😐 Seriously?' },
  { label: "🔥 YOU'RE COOKED",  text: "🔥 YOU'RE COOKED" },
  { label: "💀 I'M COOKED",     text: "💀 I'M COOKED" },
  { label: '💸 BIG SPEND',      text: '💸 BIG SPEND' },
  { label: '🪙 LOW SPEND',      text: '🪙 LOW SPEND' },
  { label: '🔒 LOCK IN',        text: '🔒 LOCK IN' },
  { label: '😮 WOW',            text: '😮 WOW' },
];

export function AuctionScreen() {
  const {
    matchId,
    playerId,
    playerRole,
    player,
    opponent,
    opponentId,
    auctionPool,
    currentAuctionIndex,
    auctionHistory,
    settings,
    timeRemaining,
    setTimeRemaining,
    setPlayerBid,
    setOpponentBid,
    lockPlayerBid,
    unlockPlayerBid,
    lockOpponentBid,
    unlockOpponentBid,
    resolveAuction,
    nextAuction,
    mode,
    applyMatchSnapshot,
    applyPlayersSnapshot,
    setPhase,
    setResultOverride,
    resetGame,
    stateVersion,
    setBidVersion,
    setStateVersion,
    addAuctionRound,
  } = useGameStore();

  const [phase, setAuctionPhase] = useState<AuctionPhase>('reveal');
  const [isRebidding, setIsRebidding] = useState(false);
  const [rebidMinimum, setRebidMinimum] = useState(0);
  const [lastResultWasRebid, setLastResultWasRebid] = useState(false);
  // Local string state for the numeric input so the user can freely type/backspace
  // without the controlled value fighting them. Committed to the store on change.
  const [bidInputValue, setBidInputValue] = useState('');
  const [pvpSecondsLeft, setPvpSecondsLeft] = useState(settings.bidTimer);
  const [displayedAuctionIndex, setDisplayedAuctionIndex] = useState(0);
  const [displayedCharacter, setDisplayedCharacter] = useState<Character | null>(null);
  const [musicMuted, setMusicMutedState] = useState(() => isMusicMuted());
  const [isLockingBid, setIsLockingBid] = useState(false);

  // Quick messages
  const [incomingMessage, setIncomingMessage] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [messageCooldown, setMessageCooldown] = useState(false);
  const messageCooldownTimerRef = useRef<number | null>(null);
  const incomingTimerRef = useRef<number | null>(null);
  // Holds the subscribed quick_msg channel so sends can reuse it
  const quickMsgChannelRef = useRef<{ send: (msg: object) => Promise<unknown> } | null>(null);

  // Capture whether we were rebidding when the bids were snapshotted —
  // needed so the result card can show penalty info in PvP (lastResultWasRebid
  // is only set in the CPU path of finalizeLockedBids so we track it here).
  const wasRebiddingWhenLockedRef = useRef(false);

  // pendingBattleIndex: set when match.status='battle' arrives so the
  // detect-resolved effect can fire even though applyMatchSnapshot is deferred.
  const [pendingBattleIndex, setPendingBattleIndex] = useState<number | null>(null);

  const phaseRef = useRef<AuctionPhase>('reveal');
  const isResolving = useRef(false);
  const finalizingRef = useRef(false);
  const lockDebounceRef = useRef<number | null>(null);
  const bidDebounceRef = useRef<number | null>(null);
  const startTimerKeyRef = useRef<string | null>(null);
  const startTimerInFlightRef = useRef(false);
  const opponentLeftRef = useRef(false);
  const playersCountRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const lastHeartbeatRef = useRef<number | null>(null);
  const preResultRef = useRef<{ character: Character } | null>(null);
  const prevServerAuctionIndexRef = useRef(-1);
  const forceLockRef = useRef<string | null>(null);
  const cpuTimerRef = useRef<number | null>(null);
  const pvpTimerStopRef = useRef<(() => void) | null>(null);
  const lastPvpSecondsRef = useRef<number>(settings.bidTimer);
  const playerBidLockedRef = useRef(false);
  const opponentBidLockedRef = useRef(false);
  const rebidNoticeShownRef = useRef(false);
  const freezeDisplayRef = useRef(false);
  const cpuProfileRef = useRef({
    aggression: 0.82 + Math.random() * 0.28,
    patience: 0.75 + Math.random() * 0.35,
    volatility: 0.18 + Math.random() * 0.22,
    chase: 0.34 + Math.random() * 0.28,
  });
  // Incremented each time a rebid round starts so timer effects re-run
  const rebidCountRef = useRef(0);
  const [rebidCount, setRebidCount] = useState(0);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { playerBidLockedRef.current = player.bidLocked; }, [player.bidLocked]);
  useEffect(() => { opponentBidLockedRef.current = opponent.bidLocked; }, [opponent.bidLocked]);
  useEffect(() => {
    if (mode !== 'pvp' || !settings.rebidMode || rebidNoticeShownRef.current) return;
    toast.info('Rebid Mode is on: ties trigger a rebid round.');
    rebidNoticeShownRef.current = true;
  }, [mode, settings.rebidMode]);

  const isHost = playerRole === 'host';
  const currentCharacter = displayedCharacter ?? auctionPool[currentAuctionIndex] ?? null;
  const activeAuctionIndex = displayedCharacter ? displayedAuctionIndex : currentAuctionIndex;
  const lastResult = auctionHistory[auctionHistory.length - 1];
  const bothPlayersJoined = Boolean(playerId && opponentId);

  // ─── DISPLAY CHARACTER SYNC ──────────────────────────────────────────────
  useEffect(() => {
    if (!auctionPool.length) return;
    if (freezeDisplayRef.current) return;
    const serverIndex = Math.max(0, Math.min(currentAuctionIndex, auctionPool.length - 1));
    if (!displayedCharacter) {
      setDisplayedAuctionIndex(serverIndex);
      setDisplayedCharacter(auctionPool[serverIndex] ?? null);
      return;
    }
    if (phase === 'result') return;
    if (displayedAuctionIndex !== serverIndex) {
      setDisplayedAuctionIndex(serverIndex);
      setDisplayedCharacter(auctionPool[serverIndex] ?? null);
    }
  }, [auctionPool, currentAuctionIndex, phase]);

  // ─── CPU BID GENERATION ─────────────────────────────────────────────────

  const generateCpuBid = useCallback(() => {
    if (!currentCharacter) return 0;
    const isHard = settings.cpuDifficulty === 'hard';
    const remaining = Math.max(1, auctionPool.length - currentAuctionIndex);
    const needed = Math.max(0, 3 - opponent.team.length);
    const desiredTeamSize = Math.min(
      auctionPool.length,
      Math.max(3, settings.battleFormat, Math.ceil(settings.auctionCount * 0.45))
    );
    const desiredNeeded = Math.max(0, desiredTeamSize - opponent.team.length);
    const mustBid = remaining <= needed + 1;
    const shouldBuildRoster = opponent.team.length < 3 || remaining <= desiredNeeded + 2;
    const remainingPool = auctionPool.slice(currentAuctionIndex);
    // Score quality using relative pool strength + team-fit gain so bids
    // track "good vs bad" better than a raw average stat.
    const statKeys = Object.keys(currentCharacter.stats);
    const statCount = Math.max(1, statKeys.length);
    const percentileByStat: number[] = [];
    let teamFitGainSum = 0;
    for (const stat of statKeys) {
      const candidateValue = currentCharacter.stats[stat] ?? 1000;
      const poolValues = remainingPool.map((c) => c.stats[stat] ?? 1000);
      const sortedValues = [...poolValues, candidateValue].sort((a, b) => a - b);
      const candidateRank = sortedValues.lastIndexOf(candidateValue);
      const candidatePct = sortedValues.length > 1
        ? candidateRank / (sortedValues.length - 1)
        : 0.5;
      percentileByStat.push(candidatePct);
      const currentTeamBest = opponent.team.reduce((best, ch) => Math.max(best, ch.stats[stat] ?? 1000), 1000);
      const teamRank = sortedValues.filter((value) => value <= currentTeamBest).length - 1;
      const teamPct = sortedValues.length > 1
        ? Math.max(0, teamRank) / (sortedValues.length - 1)
        : 0.5;
      teamFitGainSum += Math.max(0, candidatePct - teamPct);
    }
    const avgPercentile = percentileByStat.length
      ? percentileByStat.reduce((sum, value) => sum + value, 0) / percentileByStat.length
      : 0.5;
    const topTwoAvg = percentileByStat
      .slice()
      .sort((a, b) => b - a)
      .slice(0, 2)
      .reduce((sum, value) => sum + value, 0) / Math.min(2, percentileByStat.length || 1);
    const teamFitGain = teamFitGainSum / statCount;
    // quality: 0 (weak/low fit) -> 1 (elite/high fit)
    const quality = Math.max(0, Math.min(1, avgPercentile * 0.35 + topTwoAvg * 0.45 + teamFitGain * 0.20));
    const teamAdvantage = opponent.team.length - player.team.length;
    const urgency = mustBid
      ? 1
      : Math.max(0, Math.min(1, (desiredNeeded + (teamAdvantage < 0 ? 1 : 0)) / remaining));
    const reserveSlots = Math.max(1, Math.min(remaining, desiredNeeded || needed || 2));
    const perSlotBudget = opponent.coins / reserveSlots;
    const pressure = player.bidLocked && player.currentBid > 0
      ? Math.min(1, player.currentBid / Math.max(1, opponent.coins))
      : 0;
    const profile = cpuProfileRef.current;
    const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

    const rollBetween = (min: number, max: number) => {
      const low = Math.max(0, Math.min(min, max));
      const high = Math.max(low, max);
      return Math.floor(Math.random() * (high - low + 1)) + low;
    };
    const shadeBid = (target: number, floor: number, spread: number) => {
      const lowShade = 0.48 + Math.random() * 0.24;
      const highShade = 0.78 + Math.random() * spread;
      return rollBetween(
        Math.floor(Math.max(floor, target * lowShade)),
        Math.floor(Math.max(floor, target * highShade))
      );
    };

    if (isHard) {
      // Hard: strategic, but capped. It should contest value without spending the same obvious curve.
      const interest = clamp01(
        quality * 0.68 +
        urgency * 0.26 +
        teamFitGain * 0.18 +
        (Math.random() - 0.5) * profile.volatility
      );
      const skipChance = mustBid || shouldBuildRoster
        ? 0
        : Math.max(0.04, 0.32 - interest * 0.34 - profile.patience * 0.06);
      if (!isRebidding && Math.random() < skipChance) return 0;

      const floor = Math.max(isRebidding ? rebidMinimum : 0, mustBid ? Math.min(opponent.coins, 25) : 0);
      const coinCapPct = Math.min(
        mustBid ? 0.62 : 0.46,
        0.12 + interest * 0.27 + urgency * 0.10 + profile.aggression * 0.04
      );
      const slotCap = perSlotBudget * (0.62 + interest * 0.72 + urgency * 0.28);
      const ceiling = Math.max(
        floor,
        Math.min(opponent.coins * coinCapPct, slotCap, opponent.coins)
      );
      let baseBid = shadeBid(
        ceiling * (0.78 + profile.aggression * 0.12),
        floor,
        0.24
      );

      if (!mustBid && !isRebidding && quality < 0.55 && Math.random() < 0.28) {
        baseBid = Math.min(baseBid, Math.floor(ceiling * (0.38 + Math.random() * 0.20)));
      }

      if (!mustBid && !isRebidding && quality > 0.78 && Math.random() < 0.18) {
        baseBid = Math.min(
          opponent.coins,
          Math.floor(baseBid * (1.08 + Math.random() * 0.16))
        );
      }

      if (player.bidLocked && player.currentBid > 0 && !isRebidding) {
        const wantScore = interest * 0.72 + urgency * 0.22 + (shouldBuildRoster ? 0.10 : 0);
        const maxChase = Math.min(opponent.coins, Math.floor(ceiling * (1.02 + Math.random() * 0.16)));
        const outbidStep = Math.max(5, Math.floor(8 + perSlotBudget * (0.025 + Math.random() * 0.055)));
        const outbid = Math.min(opponent.coins, player.currentBid + outbidStep);
        const shouldChase = outbid <= maxChase && pressure < 0.72 && wantScore > 0.48 && Math.random() < profile.chase + interest * 0.34;
        if (shouldChase) {
          return Math.max(baseBid, outbid);
        }
        if (player.currentBid > maxChase && Math.random() < 0.72) {
          return Math.max(floor, Math.min(baseBid, maxChase));
        }
      }

      return Math.min(Math.max(baseBid, floor), opponent.coins);
    } else {
      // Easy: imperfect but not brain-dead. Lower caps and more passes.
      const interest = clamp01(
        quality * 0.60 +
        urgency * 0.22 +
        teamFitGain * 0.12 +
        (Math.random() - 0.5) * (profile.volatility + 0.12)
      );
      const skipChance = mustBid || shouldBuildRoster
        ? 0.04
        : Math.max(0.14, 0.42 - interest * 0.34);
      if (!isRebidding && Math.random() < skipChance) return 0;

      const floor = Math.max(isRebidding ? rebidMinimum : 0, mustBid ? Math.min(opponent.coins, 10) : 0);
      const coinCapPct = Math.min(
        mustBid ? 0.45 : 0.34,
        0.08 + interest * 0.21 + urgency * 0.07
      );
      const slotCap = perSlotBudget * (0.48 + interest * 0.56 + urgency * 0.20);
      const ceiling = Math.max(
        floor,
        Math.min(opponent.coins * coinCapPct, slotCap, opponent.coins)
      );
      let baseBid = shadeBid(ceiling, floor, 0.20);

      if (!mustBid && !isRebidding && Math.random() < 0.22) {
        baseBid = Math.floor(baseBid * (0.55 + Math.random() * 0.25));
      }

      if (player.bidLocked && player.currentBid > 0 && quality > 0.76 && pressure < 0.55 && Math.random() < 0.22) {
        const outbid = Math.min(opponent.coins, player.currentBid + Math.max(5, Math.floor(perSlotBudget * 0.035)));
        if (outbid <= ceiling) return Math.max(baseBid, outbid);
      }

      return Math.min(Math.max(baseBid, floor), opponent.coins);
    }
  }, [currentCharacter, settings.cpuDifficulty, settings.battleFormat, settings.auctionCount, auctionPool, currentAuctionIndex, opponent.coins, opponent.team, player.team.length, isRebidding, rebidMinimum, player.bidLocked, player.currentBid]);
  const cpuTimeRef = useRef(settings.bidTimer);
  const generateCpuBidRef = useRef<() => number>(() => 0);
  useEffect(() => { generateCpuBidRef.current = generateCpuBid; }, [generateCpuBid]);

  const startCpuTimer = useCallback(() => {
    cpuTimeRef.current = settings.bidTimer;
    setTimeRemaining(settings.bidTimer);
    if (cpuTimerRef.current) clearInterval(cpuTimerRef.current);
    cpuTimerRef.current = window.setInterval(() => {
      cpuTimeRef.current = Math.max(0, cpuTimeRef.current - 1);
      setTimeRemaining(cpuTimeRef.current);
      if (cpuTimeRef.current <= 3 && cpuTimeRef.current > 0 && lastTickRef.current !== cpuTimeRef.current) {
        playSfx('countdown_tick'); lastTickRef.current = cpuTimeRef.current;
      }
      if (cpuTimeRef.current <= 5 && cpuTimeRef.current > 0) {
        setMusicVolume(0.9);
        if (lastHeartbeatRef.current !== cpuTimeRef.current) { playSfx('heartbeat_tick', 0.4); lastHeartbeatRef.current = cpuTimeRef.current; }
      } else if (cpuTimeRef.current > 5) { restoreMusicVolume(); lastHeartbeatRef.current = null; }
      if (cpuTimeRef.current <= 0) {
        clearInterval(cpuTimerRef.current!); cpuTimerRef.current = null;
        if (!playerBidLockedRef.current) { lockPlayerBid(); playSfx('bid_lock'); }
        if (!opponentBidLockedRef.current) { lockOpponentBid(generateCpuBidRef.current()); }
      }
    }, 1000);
  }, [settings.bidTimer, setTimeRemaining, lockPlayerBid, lockOpponentBid]);

  useEffect(() => {
    if (mode !== 'cpu' || phase !== 'bidding') return;
    startCpuTimer();
    return () => { if (cpuTimerRef.current) { clearInterval(cpuTimerRef.current); cpuTimerRef.current = null; } };
  }, [mode, phase, currentAuctionIndex, startCpuTimer]);

  // ─── PvP TIMER ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp' || phase !== 'bidding' || !matchId) return;
    if (pvpTimerStopRef.current) { pvpTimerStopRef.current(); pvpTimerStopRef.current = null; }
    lastPvpSecondsRef.current = settings.bidTimer;
    setPvpSecondsLeft(settings.bidTimer);
    lastTickRef.current = null; lastHeartbeatRef.current = null;

    pvpTimerStopRef.current = pollAuctionTimer(matchId, (timerState) => {
      if (phaseRef.current !== 'bidding') return;
      if (timerState.status === 'active' && timerState.time_remaining_ms !== null) {
        const secs = Math.max(0, Math.ceil(timerState.time_remaining_ms / 1000));
        lastPvpSecondsRef.current = secs; setPvpSecondsLeft(secs); setTimeRemaining(secs);
        if (secs <= 3 && secs > 0 && lastTickRef.current !== secs) { playSfx('countdown_tick'); lastTickRef.current = secs; }
        if (secs <= 5 && secs > 0) {
          setMusicVolume(0.9);
          if (lastHeartbeatRef.current !== secs) { playSfx('heartbeat_tick', 0.4); lastHeartbeatRef.current = secs; }
        } else if (secs > 5) { restoreMusicVolume(); lastHeartbeatRef.current = null; }
      } else if (timerState.status === 'expired') {
        setPvpSecondsLeft(0); setTimeRemaining(0);
        if (isHost && matchId && phaseRef.current === 'bidding') {
          const fKey = `${currentAuctionIndex}:expired`;
          if (forceLockRef.current !== fKey) {
            forceLockRef.current = fKey;
            // Await so resolveAuctionV2 (triggered by the subsequent players
            // snapshot) doesn't race ahead of the DB write.
            void forceLockBids(matchId).then(() => {
              // After forceLock commits, directly attempt resolution so we
              // don't rely solely on the subscription round-trip timing.
              if (isHost && phaseRef.current === 'bidding' && !finalizingRef.current) {
                finalizingRef.current = true;
                void resolvePvpAuction();
              }
            });
          }
        }
      } else if (timerState.status === 'not_started') {
        if (lastPvpSecondsRef.current >= settings.bidTimer) { setPvpSecondsLeft(settings.bidTimer); setTimeRemaining(settings.bidTimer); }
      }
    }, 250);

    return () => {
      if (pvpTimerStopRef.current) { pvpTimerStopRef.current(); pvpTimerStopRef.current = null; }
      restoreMusicVolume(); lastTickRef.current = null; lastHeartbeatRef.current = null;
    };
  }, [mode, phase, matchId, currentAuctionIndex, rebidCount]);

  // ─── START AUCTION TIMER (host only) ────────────────────────────────────
  const bothPlayersJoinedRef = useRef(bothPlayersJoined);
  useEffect(() => { bothPlayersJoinedRef.current = bothPlayersJoined; }, [bothPlayersJoined]);

  useEffect(() => {
    if (mode !== 'pvp' || phase !== 'bidding' || !isHost || !matchId) return;
    if (!bothPlayersJoinedRef.current) return;
    const key = `${matchId}:${currentAuctionIndex}:${rebidCount}`;
    if (startTimerKeyRef.current === key) return;
    const t = window.setTimeout(async () => {
      if (startTimerKeyRef.current === key) return;
      startTimerKeyRef.current = key;
      startTimerInFlightRef.current = true;
      const result = await startAuctionTimer(matchId);
      if (!result.ok) { startTimerKeyRef.current = null; console.warn('[Timer] Start failed:', result.error); }
      startTimerInFlightRef.current = false;
    }, 100);
    return () => window.clearTimeout(t);
  }, [mode, phase, isHost, matchId, currentAuctionIndex, rebidCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { finalizingRef.current = false; }, [currentAuctionIndex, rebidCount]);

  // ─── PvP: capture bid snapshot ──────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp' || phase !== 'bidding') return;
    if (player.bidLocked && opponent.bidLocked && displayedCharacter) {
      preResultRef.current = {
        character: displayedCharacter,
      };
      // Capture rebid state at snapshot time so result card can show penalty info
      wasRebiddingWhenLockedRef.current = isRebidding;
    }
  }, [mode, phase, player.bidLocked, opponent.bidLocked, displayedCharacter, isRebidding]);

  useEffect(() => {
    if (mode !== 'pvp' || phase !== 'reveal') return;
    preResultRef.current = null;
    wasRebiddingWhenLockedRef.current = false;
  }, [mode, phase]);

  // ─── PvP: detect auction resolved ───────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp') return;
    const effectiveIndex = pendingBattleIndex !== null ? pendingBattleIndex : currentAuctionIndex;
    if (prevServerAuctionIndexRef.current < 0) { prevServerAuctionIndexRef.current = effectiveIndex; return; }
    if (effectiveIndex <= prevServerAuctionIndexRef.current) return;
    prevServerAuctionIndexRef.current = effectiveIndex;

    const pre = preResultRef.current;
    preResultRef.current = null;

    if (pendingBattleIndex !== null) setPendingBattleIndex(null);

    const resolvedCharacter = pre?.character ?? auctionPool[effectiveIndex - 1];
    if (!resolvedCharacter || !matchId) {
      setAuctionPhase('reveal');
      return;
    }
    freezeDisplayRef.current = true;
    setDisplayedCharacter(resolvedCharacter);
    setDisplayedAuctionIndex(Math.max(0, effectiveIndex - 1));

    // The authoritative history is written in the same transaction as the
    // index update. Bids only become visible once that transaction resolves.
    void (async () => {
      const supabase = getSupabaseClient();
      const { data, error } = supabase
        ? await supabase.from('match_auctions')
            .select('host_bid, guest_bid, winner')
            .eq('match_id', matchId)
            .eq('auction_number', effectiveIndex)
            .maybeSingle()
        : { data: null, error: new Error('Supabase unavailable') };
      if (error || !data) {
        console.error('[Auction] Could not load resolved bid result', error);
        freezeDisplayRef.current = false;
        setAuctionPhase('reveal');
        return;
      }

      const playerBid = isHost ? data.host_bid : data.guest_bid;
      const opponentBid = isHost ? data.guest_bid : data.host_bid;
      const winner: 'player' | 'opponent' | 'tie' = data.winner === 'tie'
        ? 'tie' : data.winner === playerRole ? 'player' : 'opponent';
      addAuctionRound({ character: resolvedCharacter, playerBid, opponentBid, winner });
      setLastResultWasRebid(wasRebiddingWhenLockedRef.current);
      wasRebiddingWhenLockedRef.current = false;

      restoreMusicVolume();
      if (winner === 'player') playSfx('win_auction');
      else if (winner === 'opponent') playSfx('lose_auction');
      else playSfx('tie_auction');

      setAuctionPhase('result');
      window.setTimeout(() => {
        if (phaseRef.current === 'result') { freezeDisplayRef.current = false; setAuctionPhase('reveal'); }
      }, 2500);
    })();
  }, [mode, currentAuctionIndex, pendingBattleIndex]);

  // ─── REVEAL → BIDDING ───────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'reveal') return;
    restoreMusicVolume(); playSfx('auction_reveal');
    const t = window.setTimeout(() => {
      setAuctionPhase('bidding');
      if (mode === 'cpu') { cpuTimeRef.current = settings.bidTimer; setTimeRemaining(settings.bidTimer); }
    }, 2500);
    return () => window.clearTimeout(t);
  }, [phase, currentAuctionIndex]);

  useEffect(() => {
    if (mode !== 'cpu' || phase !== 'result' || !lastResult) return;
    duckMusic(0.1, 900); playSfx('bid_reveal');
    if (lastResult.winner === 'player') playSfx('win_auction');
    else if (lastResult.winner === 'opponent') playSfx('lose_auction');
    else playSfx('tie_auction');
  }, [mode, phase, lastResult?.character?.id]);

  // ─── PvP SUBSCRIPTIONS ──────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'pvp' || !matchId) return;
    const unsubMatch = subscribeToMatch(matchId, (match) => {
      if (match.status === 'battle') {
        // Signal detect-resolved effect with the new index so it can show the
        // final result screen before we switch to BattleScreen.
        setPendingBattleIndex(match.current_auction_index);
        const pendingMatch = match;
        const checkAndApply = () => {
      if (freezeDisplayRef.current || preResultRef.current !== null || phaseRef.current === 'bidding') {
            window.setTimeout(checkAndApply, 300);
          } else {
            applyMatchSnapshot(pendingMatch);
          }
        };
        window.setTimeout(checkAndApply, 300);
        return;
      }
      applyMatchSnapshot(match);
    });
    const unsubPlayers = subscribeToPlayers(matchId, (players) => {
      applyPlayersSnapshot(players);
      if (isHost && players.length === 2 && playersCountRef.current < 2) toast.info(`${opponent.name} joined`);
      const prevCount = playersCountRef.current;
      playersCountRef.current = players.length;
      if (prevCount >= 2 && players.length < 2 && !opponentLeftRef.current) {
        const currentPhase = useGameStore.getState().phase;
        if (currentPhase === 'battle' || currentPhase === 'auction') {
          opponentLeftRef.current = true;
          setResultOverride('player');
          setPhase('results');
          toast.success('YOU WIN. Opponent forfeited.');
        }
      }
    });
    return () => { unsubMatch(); unsubPlayers(); };
  }, [mode, matchId]);

  // ─── REBID MODE ─────────────────────────────────────────────────────────
  // FIX: When the server signals a rebid (player.minimumBid > 0), we MUST
  // call unlockPlayerBid() to clear the preserveLocalLock window in the store.
  // Without this, applyPlayersSnapshot's preserveLocalLock guard swallows the
  // server's bid_locked=false update for up to 5 seconds, leaving the player
  // stuck in a locked state and unable to re-enter a bid.
  useEffect(() => {
    if (mode !== 'pvp') return;
    if (player.minimumBid > 0) {
      setIsRebidding(true);
      setRebidMinimum(player.minimumBid);
      // Bump rebid counter so PvP timer effect re-runs for the new rebid round
      rebidCountRef.current += 1;
      setRebidCount(rebidCountRef.current);
      // Force-unlock locally so the player can re-bid immediately
      if (player.bidLocked) {
        unlockPlayerBid(); // clears lastLocalBidLockAt — lets server snapshot apply
        setPlayerBid(0);   // clears lastLocalBidAt guard too
      }
    } else {
      setIsRebidding(false);
      setRebidMinimum(0);
    }
  }, [mode, player.minimumBid]);

  useEffect(() => { if (phase === 'reveal') { setIsRebidding(false); setRebidMinimum(0); } }, [phase]);

  // Keep the text input in sync with external bid changes (slider, quick-bid,
  // rebid reset). Only update if the parsed value actually differs so we don't
  // clobber the user mid-type.
  useEffect(() => {
    const parsed = bidInputValue === '' ? 0 : Number(bidInputValue);
    if (isNaN(parsed) || parsed !== player.currentBid) {
      setBidInputValue(String(player.currentBid));
    }
  }, [player.currentBid]);

  // ─── QUICK MESSAGES ─────────────────────────────────────────────────────
  // Uses `quick_msg:${matchId}` channel — distinct from `realtime:${matchId}`
  // (bid channel). Supabase only allows one subscription per channel name per
  // client; sharing the name silently kills one subscription.
  //
  // The subscribed channel is stored in quickMsgChannelRef so handleSendMessage
  // can reuse it. Creating a fresh supabase.channel() for sends does NOT work
  // because the new instance hasn't joined the WebSocket yet.
  useEffect(() => {
    if (mode !== 'pvp' || !matchId || !playerRole) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    const channel = supabase
      .channel(`quick_msg:${matchId}`)
      .on('broadcast', { event: 'quick_message' }, (payload) => {
        const p = payload.payload as { player_role: string; message: string };
        if (p.player_role === playerRole) return; // ignore own echo
        playSfx('card_flip', 0.5);
        setIncomingMessage(p.message);
        if (incomingTimerRef.current) window.clearTimeout(incomingTimerRef.current);
        incomingTimerRef.current = window.setTimeout(() => setIncomingMessage(null), 3000);
      })
      .subscribe();

    quickMsgChannelRef.current = channel as unknown as { send: (msg: object) => Promise<unknown> };

    return () => {
      supabase.removeChannel(channel);
      quickMsgChannelRef.current = null;
    };
  }, [mode, matchId, playerRole]);

  const handleSendMessage = useCallback(async (text: string) => {
    if (!matchId || !playerRole || messageCooldown) return;
    setMessageCooldown(true);
    setSentMessage(text);

    if (quickMsgChannelRef.current) {
      await quickMsgChannelRef.current.send({
        type: 'broadcast',
        event: 'quick_message',
        payload: { player_role: playerRole, message: text, timestamp: new Date().toISOString() },
      });
    }

    if (messageCooldownTimerRef.current) window.clearTimeout(messageCooldownTimerRef.current);
    messageCooldownTimerRef.current = window.setTimeout(() => {
      setMessageCooldown(false);
      setSentMessage(null);
    }, 3000);
  }, [matchId, playerRole, messageCooldown]);

  // ─── MUSIC ──────────────────────────────────────────────────────────────
  useEffect(() => { playMusic('background_music_auction'); return () => stopMusic(); }, []);
  const toggleMusic = useCallback(() => {
    const nextMuted = !musicMuted;
    setMusicMuted(nextMuted);
    setMusicMutedState(nextMuted);
  }, [musicMuted]);

  useEffect(() => {
    return () => {
      if (cpuTimerRef.current) clearInterval(cpuTimerRef.current);
      if (pvpTimerStopRef.current) pvpTimerStopRef.current();
      if (lockDebounceRef.current) clearTimeout(lockDebounceRef.current);
      if (bidDebounceRef.current) { clearTimeout(bidDebounceRef.current); bidDebounceRef.current = null; }
      if (messageCooldownTimerRef.current) clearTimeout(messageCooldownTimerRef.current);
      if (incomingTimerRef.current) clearTimeout(incomingTimerRef.current);
      restoreMusicVolume(); stopMusic();
    };
  }, []);

  // ─── BID CHANGE ─────────────────────────────────────────────────────────
  const submitLatestBid = useCallback(async () => {
    if (!matchId) return { success: false, error: 'No match found' } as const;

    const latestBid = useGameStore.getState().player.currentBid;
    const latestVersion = useGameStore.getState().bidVersion;
    const result = await submitBidV2(matchId, latestBid, latestVersion);

    if (result.success) {
      setBidVersion(result.data!.new_bid_version);
      return { success: true, bid: latestBid } as const;
    }

    if (result.needsRetry && result.currentVersion !== undefined) {
      setBidVersion(result.currentVersion);
      const retry = await submitBidV2(matchId, latestBid, result.currentVersion);
      if (retry.success) {
        setBidVersion(retry.data!.new_bid_version);
        return { success: true, bid: latestBid } as const;
      }
      return { success: false, error: retry.error ?? 'Bid update failed' } as const;
    }

    return { success: false, error: result.error ?? 'Bid update failed' } as const;
  }, [matchId, setBidVersion]);

  const handleBidChange = useCallback((newBid: number) => {
    const min = isRebidding ? rebidMinimum : 0;
    const clamped = Math.max(min, Math.min(newBid, player.coins));
    setPlayerBid(clamped);
    if (mode !== 'pvp' || !matchId) return;
    if (bidDebounceRef.current) clearTimeout(bidDebounceRef.current);
    bidDebounceRef.current = window.setTimeout(async () => {
      bidDebounceRef.current = null;
      const result = await submitLatestBid();
      if (!result.success) console.warn('[Auction] Bid update failed:', result.error);
    }, 400);
  }, [setPlayerBid, mode, matchId, submitLatestBid, isRebidding, rebidMinimum, player.coins, player.bidLocked]);

  // ─── LOCK BID ───────────────────────────────────────────────────────────
  const handleLockBid = useCallback(async () => {
    if (isLockingBid) return;
    if (player.currentBid > player.coins) { toast.error(`Bid exceeds coins (max ${player.coins})`); return; }
    if (bidDebounceRef.current) { clearTimeout(bidDebounceRef.current); bidDebounceRef.current = null; }
    setIsLockingBid(true);
    if (mode === 'pvp' && matchId) {
      const submitResult = await submitLatestBid();
      if (!submitResult.success) {
        setIsLockingBid(false);
        toast.error(submitResult.error ?? 'Bid update failed. Try again.');
        return;
      }

      lockPlayerBid();
      playSfx('bid_lock');
      const result = await lockBidV2(matchId);
      if (!result.success) {
        unlockPlayerBid();
        setIsLockingBid(false);
        toast.error(result.error ?? 'Lock failed');
        return;
      }
      setBidVersion(result.data!.new_bid_version);
      setIsLockingBid(false);
      return;
    }

    lockPlayerBid();
    playSfx('bid_lock');
    if (mode === 'cpu' && !opponent.bidLocked) {
      const delay = settings.cpuDifficulty === 'hard' ? Math.random() * 600 + 200 : Math.random() * 2000 + 500;
      window.setTimeout(() => lockOpponentBid(generateCpuBid()), delay);
    }
    setIsLockingBid(false);
  }, [isLockingBid, player.currentBid, player.coins, lockPlayerBid, unlockPlayerBid, mode, matchId, submitLatestBid, setBidVersion, generateCpuBid, opponent.bidLocked, settings.cpuDifficulty, lockOpponentBid]);

  // ─── RESOLVE AUCTION (PvP host) ─────────────────────────────────────────
  const resolvePvpAuction = useCallback(async () => {
    if (!matchId || !isHost) return;

    // Retry loop handles the race between forceLockBids completing on the
    // server and resolveAuctionV2 being called. If the server returns
    // bids_not_locked it means forceLockBids hasn't committed yet — wait
    // 600 ms and try again (up to 6 attempts = ~3.6 s total).
    let result;
    for (let retryCount = 0; retryCount < 6; retryCount++) {
      if (retryCount > 0) {
        await new Promise<void>((r) => window.setTimeout(r, 600));
      }
      let r = await resolveAuctionV2(matchId, stateVersion);
      if (r.needsRetry && r.currentVersion !== undefined) {
        setStateVersion(r.currentVersion);
        r = await resolveAuctionV2(matchId, r.currentVersion);
      }
      result = r;
      // Only retry on bids_not_locked — any other error or success exits immediately
      if (result.success || (result.error !== 'bids_not_locked' && result.error !== 'bids not locked')) break;
    }
    if (!result) { isResolving.current = false; return; }

    if (result.success) {
      setStateVersion(result.data!.new_state_version);
      const { rebid_required, minimum_bid, match_over, match_winner } = result.data!;
      if (rebid_required) {
        // FIX: unlock the host's bid so they can enter a new one for the rebid.
        // Also clear lastLocalBidLockAt so applyPlayersSnapshot doesn't re-lock.
        unlockPlayerBid();
        setPlayerBid(0);
        setIsRebidding(true);
        setRebidMinimum(minimum_bid ?? 0);
        // Bump rebid counter so the timer effects re-run for the new rebid round
        rebidCountRef.current += 1;
        setRebidCount(rebidCountRef.current);
        toast.info(`Tied bid. Rebid at least ${minimum_bid ?? 0}.`);
        setAuctionPhase('bidding');
        finalizingRef.current = false;
        isResolving.current = false;
        return;
      }
      if (match_over && match_winner) {
        // Auction ended without enough characters for battle.
        // Set resultOverride immediately from the server's authoritative winner
        // so we don't rely on snapshot arrival order (players vs match snapshots
        // can race, causing the wrong player to show as winner).
        const override =
          match_winner === 'draw' ? 'draw'
          : match_winner === 'host' ? 'player'   // host = this client (isHost is true)
          : 'opponent';
        setResultOverride(override);
      }
    } else {
      toast.error('Auction resolution failed: ' + result.error);
    }
    isResolving.current = false;
  }, [matchId, isHost, stateVersion, setStateVersion, unlockPlayerBid, setPlayerBid, setResultOverride]);

  // ─── FINALIZE LOCKED BIDS ───────────────────────────────────────────────
  const finalizeLockedBids = useCallback(() => {
    if (mode === 'pvp') {
      if (!isHost || finalizingRef.current) return;
      finalizingRef.current = true; void resolvePvpAuction(); return;
    }
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    if (player.currentBid === opponent.currentBid) {
      if (settings.rebidMode) {
        const base = player.currentBid;
        const min = Math.min(Math.max(base + 10, 10), Math.min(player.coins, opponent.coins));
        if (min < 10 || player.coins <= 0 || opponent.coins <= 0) {
          setPlayerBid(0); setOpponentBid(0); unlockPlayerBid(); unlockOpponentBid();
          setIsRebidding(false); setRebidMinimum(0); setLastResultWasRebid(false);
          addAuctionRound({ character: currentCharacter!, playerBid: player.currentBid, opponentBid: opponent.currentBid, winner: 'tie' });
          duckMusic(0.1, 900); playSfx('bid_reveal'); setAuctionPhase('result'); return;
        }
        setIsRebidding(true); setRebidMinimum(min);
        rebidCountRef.current += 1; setRebidCount(rebidCountRef.current);
        setPlayerBid(0); setOpponentBid(0); unlockPlayerBid(); unlockOpponentBid();
        setAuctionPhase('bidding'); startCpuTimer(); finalizingRef.current = false; return;
      }
      setPlayerBid(0); setOpponentBid(0); unlockPlayerBid(); unlockOpponentBid();
      setIsRebidding(false); setRebidMinimum(0); setLastResultWasRebid(false);
      addAuctionRound({ character: currentCharacter!, playerBid: player.currentBid, opponentBid: opponent.currentBid, winner: 'tie' });
      duckMusic(0.1, 900); playSfx('bid_reveal'); setAuctionPhase('result'); return;
    }
    setLastResultWasRebid(isRebidding);
    resolveAuction({ penalizeLoser: isRebidding });
    setIsRebidding(false); setRebidMinimum(0);
    duckMusic(0.1, 900); playSfx('bid_reveal'); setAuctionPhase('result');
  }, [mode, isHost, resolvePvpAuction, player.currentBid, opponent.currentBid, resolveAuction, unlockPlayerBid, unlockOpponentBid, currentCharacter, addAuctionRound, settings.rebidMode, setPlayerBid, setOpponentBid, startCpuTimer, player.coins, opponent.coins, isRebidding, setIsRebidding, setRebidMinimum]);

  // ─── BOTH LOCKED → RESOLVE ──────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'bidding') {
      if (lockDebounceRef.current) { clearTimeout(lockDebounceRef.current); lockDebounceRef.current = null; }
      return;
    }
    if (!player.bidLocked || !opponent.bidLocked) return;
    if (lockDebounceRef.current) clearTimeout(lockDebounceRef.current);
    lockDebounceRef.current = window.setTimeout(() => {
      if (!player.bidLocked || !opponent.bidLocked) return;
      finalizeLockedBids();
    }, 200);
    return () => { if (lockDebounceRef.current) { clearTimeout(lockDebounceRef.current); lockDebounceRef.current = null; } };
  }, [phase, player.bidLocked, opponent.bidLocked, finalizeLockedBids]);

  const handleNextAuction = () => { if (mode !== 'cpu') return; setAuctionPhase('reveal'); nextAuction(); };
  const handleLeaveGame = () => { if (mode === 'pvp' && matchId) void forfeitMatch(matchId); resetGame(); };

  const displaySeconds = mode === 'pvp' ? pvpSecondsLeft : timeRemaining;
  const isBidDisabled = phase === 'result' || phase === 'reveal' || player.bidLocked || (mode === 'pvp' && !bothPlayersJoined);
  const isLockDisabled = phase !== 'bidding' || player.bidLocked || isLockingBid || (mode === 'pvp' && !bothPlayersJoined);
  const quickBidPercents = [0, 10, 25, 50, 75, 100];

  if (!currentCharacter) return null;

  return (
    // h-[100dvh] uses the dynamic viewport height on mobile (accounts for browser chrome).
    // Fallback h-screen for browsers that don't support dvh.
    <div className="h-screen h-[100dvh] flex flex-col overflow-hidden bg-auction">

      {/* ── INCOMING MESSAGE OVERLAY ─────────────────────────────────────────
          The fixed anchor wrapper is always in the DOM so AnimatePresence can
          orchestrate the exit animation. Only the inner motion.div mounts/unmounts. */}
      <div style={{ position: 'fixed', top: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9999, pointerEvents: 'none' }}>
        <AnimatePresence>
          {incomingMessage && (
            <motion.div
              key={incomingMessage}
              initial={{ opacity: 0, y: -10, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 400, damping: 24 }}
              className="rounded-2xl border-2 border-opponent/70 bg-background/95 backdrop-blur-sm px-4 py-2 text-sm font-display font-bold text-opponent shadow-2xl whitespace-nowrap"
            >
              {opponent.name}: {incomingMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── SENT MESSAGE OVERLAY ─────────────────────────────────────────────
          Offset below incoming so both can coexist without overlap. */}
      <div style={{ position: 'fixed', top: 58, left: 0, right: 0, display: 'flex', justifyContent: 'center', zIndex: 9998, pointerEvents: 'none' }}>
        <AnimatePresence>
          {sentMessage && (
            <motion.div
              key={sentMessage}
              initial={{ opacity: 0, y: -10, scale: 0.88 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 400, damping: 24 }}
              className="rounded-2xl border-2 border-player/60 bg-background/90 backdrop-blur-sm px-4 py-2 text-sm font-display font-bold text-player shadow-xl whitespace-nowrap"
            >
              You: {sentMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── HEADER ───────────────────────────────────────────────────────────
          Three-column flex layout. Center column is a fixed-width shrink-0
          so it never moves regardless of how wide the side columns grow.
          This replaces the previous absolute-centered approach that broke
          when chips made the left column taller than the header min-height. */}
      <header className="shrink-0 flex items-stretch gap-1.5 px-3 py-2 bg-background/60 backdrop-blur-sm border-b border-border/20">

        {/* LEFT — YOU */}
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {/* Name + coins row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-player font-display font-bold text-xs leading-none">YOU</span>
            <CoinBadge amount={player.coins} />
          </div>
          {/* Lock status */}
          <span className={`text-[9px] font-semibold uppercase leading-none ${player.bidLocked ? 'text-success' : 'text-muted-foreground'}`}>
            {player.bidLocked ? '✓ Locked' : 'Bidding...'}
          </span>
          {/* Team portrait slots — compact w-7 h-7 */}
          <div className="flex gap-1 mt-0.5">
            {player.team.map((char) => (
              <div key={char.id} className="w-7 h-7 rounded bg-player/20 border border-player/40 overflow-hidden shrink-0">
                <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url("${encodeURI(char.portrait)}")` }} />
              </div>
            ))}
            {[...Array(Math.max(0, 3 - player.team.length))].map((_, i) => (
              <div key={i} className="w-7 h-7 rounded bg-secondary/50 border border-border shrink-0" />
            ))}
          </div>
        </div>

        {/* CENTER — auction counter, fixed width so it can't drift */}
        <div className="shrink-0 w-14 flex flex-col items-center justify-center text-center select-none pointer-events-none">
          <p className="text-[8px] text-muted-foreground leading-none">Auction</p>
          <p className="font-mono font-bold text-base text-primary leading-tight">
            {Math.min(activeAuctionIndex + 1, Math.max(1, auctionPool.length))} / {auctionPool.length}
          </p>
          <p className={`text-[8px] font-semibold leading-none mt-0.5 ${settings.rebidMode ? 'text-warning' : 'text-muted-foreground'}`}>
            {settings.rebidMode ? 'REBID ON' : 'REBID OFF'}
          </p>
        </div>

        {/* RIGHT — OPPONENT */}
        <div className="flex-1 min-w-0 flex flex-col gap-1 items-end">
          {/* Leave button + opponent name */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMusic}
              aria-label={musicMuted ? 'Turn site audio on' : 'Turn site audio off'}
              title={musicMuted ? 'Turn site audio on' : 'Turn site audio off'}
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
            >
              {musicMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLeaveGame}
              className="h-6 px-2 text-[10px] text-muted-foreground hover:text-foreground leading-none"
            >
              Leave
            </Button>
          </div>
          {/* Coins + name */}
          <div className="flex items-center gap-1.5 justify-end flex-wrap">
            <CoinBadge amount={opponent.coins} />
            <span className="text-opponent font-display font-bold text-xs leading-none truncate max-w-[72px]">{opponent.name}</span>
          </div>
          {/* Lock status */}
          <span className={`text-[9px] font-semibold uppercase leading-none ${opponent.bidLocked ? 'text-success' : 'text-muted-foreground'}`}>
            {opponent.bidLocked ? '✓ Locked' : 'Waiting...'}
          </span>
          {/* Team portrait slots */}
          <div className="flex gap-1 mt-0.5 justify-end">
            {opponent.team.map((char) => (
              <div key={char.id} className="w-7 h-7 rounded bg-opponent/20 border border-opponent/40 overflow-hidden shrink-0">
                <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url("${encodeURI(char.portrait)}")` }} />
              </div>
            ))}
            {[...Array(Math.max(0, 3 - opponent.team.length))].map((_, i) => (
              <div key={i} className="w-7 h-7 rounded bg-secondary/50 border border-border shrink-0" />
            ))}
          </div>
        </div>

      </header>

      {/* ── QUICK MESSAGE CHIPS STRIP (PvP only) ────────────────────────────
          A single horizontal-scroll row below the header. No wrapping =
          zero extra vertical height regardless of screen width. Both players
          see this strip since mode === 'pvp' is true for both clients. */}
      {mode === 'pvp' && (
        <div className="shrink-0 flex gap-1.5 overflow-x-auto px-3 py-2 border-b border-border/20 bg-background/30 scrollbar-none">
          {QUICK_MESSAGES.map(({ label, text }) => {
            const sent = sentMessage === text;
            return (
              <button
                key={text}
                onClick={() => void handleSendMessage(text)}
                disabled={messageCooldown}
                className={[
                  'shrink-0 rounded-full px-2.5 py-1 text-[10px] font-display font-semibold border transition-all leading-none select-none touch-manipulation',
                  sent
                    ? 'border-player/60 bg-player/20 text-player'
                    : messageCooldown
                      ? 'border-border/30 text-muted-foreground/40 cursor-not-allowed opacity-50'
                      : 'border-border/50 bg-secondary/40 text-muted-foreground active:scale-95 active:bg-player/10 active:text-player',
                ].join(' ')}
              >
                {sent ? `✓ ${label}` : label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── MAIN ─────────────────────────────────────────────────────────────
          flex-1 overflow-y-auto makes all bidding controls reachable on
          small phones without the layout overflowing the viewport. */}
      <main className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center justify-center min-h-full px-4 py-4">
          <AnimatePresence mode="wait">

            {/* REVEAL */}
            {phase === 'reveal' && (
              <motion.div key="reveal" className="flex flex-col items-center gap-4 w-full max-w-sm text-center"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <motion.p className="text-base text-muted-foreground font-display" initial={{ y: -16 }} animate={{ y: 0 }}>
                  Character Incoming...
                </motion.p>
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                >
                  <CharacterPortrait character={currentCharacter} size="xl" showName showVerse />
                </motion.div>
                <motion.h2
                  className="text-xl font-display font-bold text-foreground"
                  initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
                >
                  {currentCharacter.name}
                </motion.h2>
              </motion.div>
            )}

            {/* BIDDING */}
            {phase === 'bidding' && (
              <motion.div key="bidding" className="flex flex-col items-center gap-3 w-full max-w-md"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>

                <CharacterPortrait character={currentCharacter} size="md" showName showVerse />

                <TimerDisplay seconds={displaySeconds} maxSeconds={settings.bidTimer} />

                {isRebidding && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    className="w-full bg-warning/20 border-2 border-warning rounded-xl p-3 text-center"
                  >
                    <p className="flex items-center justify-center gap-1.5 font-display font-bold text-warning text-sm"><RotateCcw className="h-4 w-4" aria-hidden />REBID REQUIRED</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Min bid: <span className="font-mono font-bold text-warning">{rebidMinimum}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Loser forfeits their bid as penalty.</p>
                  </motion.div>
                )}

                {/* Bid display */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xs text-muted-foreground">Your Bid</span>
                    {isRebidding && (
                      <span className="text-[9px] font-bold uppercase text-warning bg-warning/20 border border-warning/40 rounded-full px-1.5 py-0.5">Rebid</span>
                    )}
                  </div>
                  <p className="font-mono font-bold text-4xl text-primary leading-tight">{player.currentBid.toLocaleString()}</p>
                </div>

                <div className="w-full space-y-2.5">
                  {/* Slider — py-3 makes the touch target taller on mobile */}
                  <div className="py-3">
                    <Slider
                      value={[player.currentBid]}
                      onValueChange={([v]) => void handleBidChange(v)}
                      max={player.coins} min={isRebidding ? rebidMinimum : 0} step={1}
                      disabled={isBidDisabled} className="w-full"
                    />
                  </div>

                  {/* Numeric input — uses local string state so backspace/partial
                      typing works freely without the controlled value clamping mid-edit. */}
                  <Input
                    type="number" inputMode="numeric"
                    min={isRebidding ? rebidMinimum : 0} max={player.coins} step={1}
                    value={bidInputValue}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setBidInputValue(raw);
                      const n = raw === '' ? 0 : Number(raw);
                      if (!isNaN(n)) void handleBidChange(Math.min(Math.max(n, isRebidding ? rebidMinimum : 0), player.coins));
                    }}
                    onBlur={() => {
                      // On blur, snap display to the committed store value
                      setBidInputValue(String(player.currentBid));
                    }}
                    disabled={isBidDisabled}
                    className="text-center font-mono text-lg h-12 touch-manipulation"
                  />

                  {/* Quick-bid percent buttons — 3 wide, min-h-[44px] for iOS */}
                  <div className="grid grid-cols-3 gap-1.5">
                    {quickBidPercents.map((pct) => (
                      <Button
                        key={pct}
                        variant="outline"
                        onClick={() => void handleBidChange(pct === 100 ? player.coins : Math.floor(player.coins * pct / 100))}
                        disabled={isBidDisabled}
                        className="font-mono h-11 text-xs touch-manipulation active:scale-95"
                      >
                        {pct === 0 ? '0' : pct === 100 ? 'ALL IN' : `${pct}%`}
                      </Button>
                    ))}
                  </div>

                  {/* Lock button — tall and prominent */}
                  <Button
                    size="lg"
                    onClick={handleLockBid}
                    disabled={isLockDisabled}
                    className="w-full h-14 text-lg font-display font-bold bg-primary hover:bg-primary/90 touch-manipulation active:scale-[0.98]"
                  >
                    {player.bidLocked ? '✓ Bid Locked' : isLockingBid ? 'Locking...' : <><Lock className="mr-2 h-5 w-5" aria-hidden />Lock In Bid</>}
                  </Button>

                  {/* Lock status indicators */}
                  <div className="flex justify-center gap-8 text-xs">
                    <span className={player.bidLocked ? 'text-success font-semibold' : 'text-muted-foreground'}>
                      {player.bidLocked ? '✓ You' : '○ You'}
                    </span>
                    <span className={opponent.bidLocked ? 'text-success font-semibold' : 'text-muted-foreground'}>
                      {opponent.bidLocked ? `✓ ${opponent.name}` : `○ ${opponent.name}`}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* RESULT */}
            {phase === 'result' && lastResult && (
              <motion.div key="result" className="flex flex-col items-center gap-4 text-center w-full max-w-sm"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>

                <CharacterPortrait
                  character={lastResult.character} size="lg"
                  winner={lastResult.winner === 'player'} loser={lastResult.winner === 'opponent'} showName
                />

                {/* Bid comparison */}
                <div className="flex items-center gap-8 justify-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-0.5">Your Bid</p>
                    <p className={`font-mono font-bold text-2xl ${lastResult.winner === 'player' ? 'text-success text-glow-success' : 'text-foreground'}`}>
                      {lastResult.playerBid.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-muted-foreground font-bold">vs</div>
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-0.5">Opponent</p>
                    <p className={`font-mono font-bold text-2xl ${lastResult.winner === 'opponent' ? 'text-opponent text-glow-opponent' : 'text-foreground'}`}>
                      {lastResult.opponentBid.toLocaleString()}
                    </p>
                  </div>
                </div>

                <motion.div className="w-full" initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
                  {lastResult.winner === 'player' && (
                    <div className="text-success text-xl font-display font-bold text-glow-success">
                      YOU WIN!
                      <p className="text-sm font-normal mt-1">{lastResult.character.name} joins your team!</p>
                      {lastResultWasRebid && lastResult.opponentBid > 0 && (
                        <motion.div
                          className="mt-3 rounded-xl border-2 border-warning bg-warning/15 px-4 py-2"
                          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5, type: 'spring' }}
                        >
                          <p className="text-sm font-bold text-warning">Penalty: {opponent.name} loses {lastResult.opponentBid.toLocaleString()} coins!</p>
                        </motion.div>
                      )}
                    </div>
                  )}
                  {lastResult.winner === 'opponent' && (
                    <div className="text-opponent text-xl font-display font-bold">
                      OPPONENT WINS
                      <p className="text-sm font-normal mt-1 text-muted-foreground">{lastResult.character.name} goes to them.</p>
                      {lastResultWasRebid && lastResult.playerBid > 0 && (
                        <motion.div
                          className="mt-3 rounded-xl border-2 border-destructive bg-destructive/15 px-4 py-2"
                          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5, type: 'spring' }}
                        >
                          <p className="text-sm font-bold text-destructive">Penalty: you lose {lastResult.playerBid.toLocaleString()} coins!</p>
                        </motion.div>
                      )}
                    </div>
                  )}
                  {lastResult.winner === 'tie' && (
                    <div className="text-primary text-xl font-display font-bold">
                      TIE!
                      <p className="text-sm font-normal mt-1 text-muted-foreground">No one gets {lastResult.character.name}.</p>
                      {settings.rebidMode && (
                        <motion.div
                          className="mt-3 rounded-xl border-2 border-warning bg-warning/15 px-4 py-2"
                          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5, type: 'spring' }}
                        >
                          <p className="text-sm font-bold text-warning">Tie. Character skipped.</p>
                        </motion.div>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Auction history so far (last 3 rounds, scrollable) */}
                {auctionHistory.length > 1 && (
                  <div className="w-full rounded-xl border border-border/40 bg-secondary/20 overflow-hidden">
                    <p className="text-[9px] uppercase tracking-widest text-muted-foreground px-3 py-1 border-b border-border/30">Previous Rounds</p>
                    <div className="divide-y divide-border/20 max-h-28 overflow-y-auto">
                      {[...auctionHistory].reverse().slice(0, 6).map((r, i) => (
                        <div key={i} className={`flex items-center gap-2 px-3 py-1 text-[10px] ${i === 0 ? 'bg-primary/5' : ''}`}>
                          <span className={`shrink-0 font-bold ${r.winner === 'player' ? 'text-success' : r.winner === 'opponent' ? 'text-opponent' : 'text-muted-foreground'}`}>
                            {r.winner === 'player' ? '✓' : r.winner === 'opponent' ? '✗' : '—'}
                          </span>
                          <span className="flex-1 truncate text-muted-foreground">{r.character.name}</span>
                          <span className="font-mono text-muted-foreground/70 shrink-0">{r.playerBid} / {r.opponentBid}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {mode === 'cpu' ? (
                  <Button size="lg" onClick={handleNextAuction} className="w-full h-14 text-base font-display font-bold touch-manipulation">
                    Next Auction →
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground animate-pulse">Next auction loading...</p>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </main>

    </div>
  );
}
